import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '../common/button';
import { EmptyState } from '../common/empty-state';
import { BETSLIP_MODES, type BetslipMode } from './Betslip.modes';

export interface BetslipTotal {
  label: string;
  value: string;
  /** Emphasises the row, used for the potential payout. */
  emphasis?: boolean;
}

export interface BetslipProps {
  mode?: BetslipMode;
  onModeChange?: (mode: BetslipMode) => void;
  /** Selection rows; when absent the empty state from the design system is shown. */
  children?: ReactNode;
  selectionCount?: number;
  totals?: BetslipTotal[];
  /** Stake input and quick-stake buttons. */
  stake?: ReactNode;
  submitLabel?: string;
  submitting?: boolean;
  onSubmit?: () => void;
  className?: string;
}

/**
 * The betslip chrome: mode tabs, a scrollable selection area, totals and the
 * submit bar. Stake handling and bet validation belong to the betslip feature,
 * which fills the slots.
 */
export function Betslip({
  mode = 'single',
  onModeChange,
  children,
  selectionCount = 0,
  totals = [],
  stake,
  submitLabel = 'Place bet',
  submitting = false,
  onSubmit,
  className,
}: BetslipProps) {
  const empty = selectionCount === 0;

  return (
    <section
      aria-label="Betslip"
      className={cn(
        'flex min-h-0 flex-col rounded-md border border-border-subtle bg-bg-surface',
        className,
      )}
    >
      <div
        role="tablist"
        aria-label="Bet type"
        className="flex gap-1 border-b border-border-subtle p-2"
      >
        {BETSLIP_MODES.map(({ value, label }) => (
          <button
            key={value}
            role="tab"
            type="button"
            aria-selected={mode === value}
            onClick={() => onModeChange?.(value)}
            className={cn(
              'flex-1 rounded-sm px-3 py-2 text-label-m transition-colors',
              mode === value
                ? 'bg-action-primary font-bold text-text-on-action'
                : 'text-text-secondary hover:bg-bg-elevated',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {empty ? (
          <EmptyState
            icon="betslip-empty"
            title="Your betslip is empty"
            description="Pick an odds selection to start building a bet."
            className="border-0 bg-transparent"
          />
        ) : (
          <div className="flex flex-col gap-2">{children}</div>
        )}
      </div>

      {!empty && (
        <footer className="flex flex-col gap-3 border-t border-border-subtle p-3">
          {stake}

          {totals.length > 0 && (
            <dl className="flex flex-col gap-1">
              {totals.map((total) => (
                <div key={total.label} className="flex items-baseline justify-between gap-2">
                  <dt
                    className={cn(
                      'text-body-s',
                      total.emphasis ? 'text-text-primary' : 'text-text-secondary',
                    )}
                  >
                    {total.label}
                  </dt>
                  <dd
                    className={cn(
                      'tabular-nums',
                      total.emphasis
                        ? 'text-body-l font-bold text-text-primary'
                        : 'text-body-s text-text-secondary',
                    )}
                  >
                    {total.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          <Button size="lg" fullWidth loading={submitting} onClick={onSubmit}>
            {submitLabel}
          </Button>
        </footer>
      )}
    </section>
  );
}
